/**
 * XML request builders for Player API
 *
 * Creates well-formed SOAP XML requests with automatic validation.
 *
 * @example
 * const passengersXml = buildPassengersXml({
 *   adults: 2,
 *   children: [{ age: 8 }]
 * });
 */

import { PLAYER_API_CONFIG } from '@/constants/player/config';
import {
  validatePassengerConfig,
  validatePeriodConfig,
  type PassengerConfig,
  type PeriodConfig,
} from './xmlValidation';

const { DEFAULT_CURRENCY, DEFAULT_LANG, DEFAULT_TREE_ID, TOUR_OPERATOR, DEFAULT_PRODUCT_TYPE } =
  PLAYER_API_CONFIG;

/**
 * Calculates number of days between two dates
 *
 * @example
 * calculateDaysBetween('2025-11-15', '2025-11-28') // 13
 */
function calculateDaysBetween(startDate: string, endDate: string): number {
  const start = new Date(startDate);
  const end = new Date(endDate);
  const diffTime = Math.abs(end.getTime() - start.getTime());
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
}

/**
 * Formats date for SOAP API (ISO 8601 with time)
 *
 * @example
 * formatSoapDate('2025-11-15') // '2025-11-15T00:00:00.000Z'
 */
function formatSoapDate(date: string): string {
  return `${date}T00:00:00.000Z`;
}

/**
 * Converts days to ISO 8601 duration format
 *
 * @example
 * toDuration(7) // 'P7D'
 */
function toDuration(days: number): string {
  return `P${days}D`;
}

/**
 * Escapes XML special characters
 *
 * @example
 * escapeXml('Ben & Jerry\'s') // 'Ben &amp; Jerry&apos;s'
 */
function escapeXml(unsafe: string): string {
  return unsafe
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');
}

/**
 * Builds the <bewo:Rooms> XML element for multi-room bookings
 *
 * @param rooms - Array of room assignments with passenger IDs
 * @returns XML string with <bewo:Rooms> element, or empty element for single room
 *
 * @throws Error if validation fails
 *
 * @example Single room (empty result)
 * buildRoomsXml([]) // Returns: '<bewo:Rooms/>'
 *
 * @example Multi-room
 * buildRoomsXml([
 *   { passengerIds: [1, 2] },
 *   { passengerIds: [3, 4] }
 * ])
 * // Returns:
 * // <bewo:Rooms>
 * //   <bewo:RoomRequest>
 * //     <bewo:Passengers>
 * //       <bewo:PassengerId>1</bewo:PassengerId>
 * //       <bewo:PassengerId>2</bewo:PassengerId>
 * //     </bewo:Passengers>
 * //   </bewo:RoomRequest>
 * //   <bewo:RoomRequest>
 * //     <bewo:Passengers>
 * //       <bewo:PassengerId>3</bewo:PassengerId>
 * //       <bewo:PassengerId>4</bewo:PassengerId>
 * //     </bewo:Passengers>
 * //   </bewo:RoomRequest>
 * // </bewo:Rooms>
 */
export function buildRoomsXml(rooms: Array<{ passengerIds: number[] }>): string {
  if (rooms.length === 0 || rooms.length === 1) {
    return '<bewo:Rooms/>';
  }

  // Validate rooms
  const seenPassengerIds = new Set<number>();
  for (const room of rooms) {
    if (room.passengerIds.length === 0) {
      throw new Error('Room must have at least one passenger');
    }

    for (const passengerId of room.passengerIds) {
      if (seenPassengerIds.has(passengerId)) {
        throw new Error(`Duplicate passenger ID found: ${passengerId}`);
      }
      seenPassengerIds.add(passengerId);
    }
  }

  const roomRequests = rooms
    .map((room) => {
      const passengerIdElements = room.passengerIds
        .map((id) => `            <bewo:PassengerId>${id}</bewo:PassengerId>`)
        .join('\n');

      return `        <bewo:RoomRequest>
            <bewo:Passengers>
${passengerIdElements}
            </bewo:Passengers>
        </bewo:RoomRequest>`;
    })
    .join('\n');

  return `    <bewo:Rooms>
${roomRequests}
    </bewo:Rooms>`;
}

/**
 * Builds passengers XML section with validation
 *
 * Creates passenger list for SOAP request with automatic validation.
 *
 * @throws ValidationError if passenger config is invalid
 *
 * @example
 * const xml = buildPassengersXml({
 *   adults: 2,
 *   children: [{ age: 8 }, { age: 12 }]
 * });
 */
export function buildPassengersXml(config: PassengerConfig): string {
  validatePassengerConfig(config);

  let xml = '';
  let id = 1;

  if (config.passengers && config.passengers.length > 0) {
    for (const passenger of config.passengers) {
      const code = passenger.type === 'adult' ? 'Adult' : 'Child';
      xml += `
      <bewo:Passenger>
        <bewo:Id>${id++}</bewo:Id>
        <bewo:Age>${passenger.age}</bewo:Age>
        <bewo:PassengerCode>${code}</bewo:PassengerCode>
      </bewo:Passenger>`;
    }
    return xml;
  }

  // Fallback: adults first (age 30), then children. Used by single-room
  // searches that don't supply a passenger ordering.
  for (let i = 0; i < config.adults; i++) {
    xml += `
      <bewo:Passenger>
        <bewo:Id>${id++}</bewo:Id>
        <bewo:Age>30</bewo:Age>
        <bewo:PassengerCode>Adult</bewo:PassengerCode>
      </bewo:Passenger>`;
  }

  for (const child of config.children) {
    xml += `
      <bewo:Passenger>
        <bewo:Id>${id++}</bewo:Id>
        <bewo:Age>${child.age}</bewo:Age>
        <bewo:PassengerCode>Child</bewo:PassengerCode>
      </bewo:Passenger>`;
  }

  return xml;
}

/**
 * Builds period XML section with validation
 *
 * Creates period/duration XML for SOAP request with automatic validation.
 *
 * @throws ValidationError if period config is invalid
 *
 * With `durations` the gateway is allowed to return a range of trip lengths and
 * `preferredDurations` ranks some of them first (see `durationPlan.ts`). The
 * user's `Start.Date` / `End.Date` never move — only the offsets widen, and they
 * widen to the *shortest* allowed length, otherwise a short stay leaving early
 * falls outside the return window even though it fits the user's dates.
 *
 * @example
 * const xml = buildPeriodXml({
 *   start: '2025-11-15',
 *   end: '2025-11-28',
 *   duration: 7
 * });
 */
export function buildPeriodXml(config: PeriodConfig): string {
  validatePeriodConfig(config);

  const effectiveDurations =
    config.durations && config.durations.length > 0 ? config.durations : [config.duration];

  const totalDays = calculateDaysBetween(config.start, config.end);
  const flexibilityDays = Math.max(0, totalDays - Math.min(...effectiveDurations));

  const durationsXml = effectiveDurations
    .map((days) => `        <bewo:Duration>${toDuration(days)}</bewo:Duration>`)
    .join('\n');

  const preferredXml =
    config.preferredDurations && config.preferredDurations.length > 0
      ? `
      <bewo:PreferredDurations>
${config.preferredDurations
  .map((days) => `        <bewo:PreferredDuration>${toDuration(days)}</bewo:PreferredDuration>`)
  .join('\n')}
      </bewo:PreferredDurations>`
      : '';

  return `
    <bewo:Period>
      <bewo:Start>
        <bewo:PreOffset>P0D</bewo:PreOffset>
        <bewo:Date>${formatSoapDate(config.start)}</bewo:Date>
        <bewo:PostOffset>${toDuration(flexibilityDays)}</bewo:PostOffset>
      </bewo:Start>
      <bewo:End>
        <bewo:PreOffset>${toDuration(flexibilityDays)}</bewo:PreOffset>
        <bewo:Date>${formatSoapDate(config.end)}</bewo:Date>
        <bewo:PostOffset>P0D</bewo:PostOffset>
      </bewo:End>
      <bewo:Durations>
${durationsXml}
      </bewo:Durations>${preferredXml}
    </bewo:Period>`;
}

/**
 * Builds the period XML section for GetFilters
 *
 * Only a start and its `PostOffset`: GetFilters reads the window as a range of
 * *arrival* days, ignores `Period.End` (garbled results when sent) and applies
 * no durations. `PostOffset P0D` pins the answer to that single arrival day;
 * without it the gateway widens the window and mixes in neighbouring days.
 *
 * @example
 * buildFiltersPeriodXml('2026-12-20', 0) // <bewo:Period>…<bewo:PostOffset>P0D</bewo:PostOffset>…
 */
export function buildFiltersPeriodXml(start: string, postOffsetDays: number): string {
  return `
    <bewo:Period>
      <bewo:Start>
        <bewo:Date>${formatSoapDate(start)}</bewo:Date>
        <bewo:PostOffset>${toDuration(Math.max(0, postOffsetDays))}</bewo:PostOffset>
      </bewo:Start>
    </bewo:Period>`;
}

/**
 * Builds GeoLocation XML section
 *
 * Creates geographic filter XML for SOAP request.
 *
 * @example
 * buildGeoLocationXml('ES') // <bewo:GeoLocation>...
 * buildGeoLocationXml() // <bewo:GeoLocation xsi:nil="true"/>
 */
export function buildGeoLocationXml(countryCode?: string): string {
  if (!countryCode) {
    return '<bewo:GeoLocation xsi:nil="true"/>';
  }

  return `
    <bewo:GeoLocation>
      <bewo:CountryCode>${escapeXml(countryCode)}</bewo:CountryCode>
    </bewo:GeoLocation>`;
}

/**
 * Builds GroupIds XML section
 *
 * Multi-country filtering goes through the ProductGroups tree (TreeId=1), not GeoLocation.
 *
 * @example
 * buildGroupIdsXml([10001, 10017]) // <bewo:GroupIds><bewo:GroupId>...
 * buildGroupIdsXml([]) // <bewo:GroupIds/>
 */
export function buildGroupIdsXml(groupIds?: number[]): string {
  if (!groupIds || groupIds.length === 0) {
    return '<bewo:GroupIds/>';
  }

  const groupIdsXml = groupIds.map((id) => `      <bewo:GroupId>${id}</bewo:GroupId>`).join('\n');

  return `
    <bewo:GroupIds>
${groupIdsXml}
    </bewo:GroupIds>`;
}

/**
 * Builds AccommodationProperties XML section
 *
 * Creates filter XML for accommodation properties like facilities, board types, etc.
 *
 * @example
 * buildAccommodationPropertiesXml(['UnitFacilities.SeaView', 'BoardType.AllInclusive'])
 * // Returns: <bewo:AccommodationProperties>...</bewo:AccommodationProperties>
 *
 * @example
 * buildAccommodationPropertiesXml([]) // Returns: ''
 * buildAccommodationPropertiesXml() // Returns: ''
 */
export function buildAccommodationPropertiesXml(properties?: string[]): string {
  if (!properties || properties.length === 0) {
    return '';
  }

  const propertiesXml = properties
    .map((prop) => `<bewo:AccommodationProperty>${escapeXml(prop)}</bewo:AccommodationProperty>`)
    .join('\n                  ');

  return `
               <bewo:AccommodationProperties>
                  ${propertiesXml}
               </bewo:AccommodationProperties>`;
}

/**
 * Builds base request header common to all endpoints
 *
 * Creates standard header section for SOAP requests.
 * The authKey parameter should be an empty string when using the API gateway,
 * as authentication is not required.
 *
 * @param authKey - Authentication key (use empty string for gateway)
 * @param productType - Product type (defaults to 'Hotel')
 *
 * @example
 * const header = buildRequestHeader('', 'Hotel');
 */
export function buildRequestHeader(
  authKey: string,
  productType: string = DEFAULT_PRODUCT_TYPE
): string {
  return `
    <bewo:AuthKey>${escapeXml(authKey)}</bewo:AuthKey>
    <bewo:Currency>${DEFAULT_CURRENCY}</bewo:Currency>
    <bewo:Lang>${DEFAULT_LANG}</bewo:Lang>
    <bewo:ProductType>${escapeXml(productType)}</bewo:ProductType>
    <bewo:TouroperatorCodes>
      <bewo:TouroperatorCode>${TOUR_OPERATOR}</bewo:TouroperatorCode>
    </bewo:TouroperatorCodes>
    <bewo:TreeId>${DEFAULT_TREE_ID}</bewo:TreeId>`;
}

export type { PassengerConfig, PeriodConfig };
